/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import DatePicker from 'react-datepicker'
import moment from 'moment'

export class DateRange extends React.PureComponent {
  static propTypes = {
    startDate: PropTypes.instanceOf(moment),
    endDate: PropTypes.instanceOf(moment),
    onStartDateChange: PropTypes.func.isRequired,
    onEndDateChange: PropTypes.func.isRequired,
    minDate: PropTypes.string,
    maxDate: PropTypes.instanceOf(moment),
    dateFormat: PropTypes.string,
    align: PropTypes.oneOf(["vertical", "horizontal"]).isRequired,
    separatorText: PropTypes.string,
    disableEndDateInputWhenNoStartDate: PropTypes.bool,
    showTimeSelect: PropTypes.bool,
    timeFormat: PropTypes.string,
    timeIntervals: PropTypes.number,
    peekNextMonth: PropTypes.bool,
    showMonthDropdown: PropTypes.bool,
    showYearDropdown: PropTypes.bool,
    dropdownMode: PropTypes.oneOf(['dropdown', 'select']),
    disabled: PropTypes.bool,
    forceCloseOnSelect: PropTypes.bool,
    startDateClearable: PropTypes.bool,
    endDateClearable: PropTypes.bool
  }

  static defaultProps = {
    align: "horizontal",
    startDateClearable: true,
    endDateClearable: true
  }

  constructor(props) {
    super(props)

    this.startDateRef = React.createRef()
    this.endDateRef = React.createRef()
  }

  render() {
    let { startDate, endDate, onStartDateChange, onEndDateChange, minDate, maxDate, dateFormat,
      align, separatorText, disableEndDateInputWhenNoStartDate, showTimeSelect, timeFormat,
      peekNextMonth, showMonthDropdown, showYearDropdown, dropdownMode, disabled, timeIntervals,
      startDateClearable, endDateClearable, forceCloseOnSelect, startDatePlaceholder, endDatePlaceholder } = this.props

    let className = `slds-input${disabled ? ' inactive' : ''}`

    if (align === "vertical") {
      return (
        <React.Fragment>
          <DatePicker
            id='startDate'
            ref={this.startDateRef}
            selected={startDate}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            onChange={forceCloseOnSelect
              ? date => {
                  this.startDateRef.current.setOpen(false)
                  onStartDateChange(date)
                }
              : onStartDateChange}
            placeholderText={startDatePlaceholder || "Start date"}
            dateFormat={dateFormat || "DD/MM/YYYY"}
            minDate={minDate ? moment(minDate) : undefined}
            maxDate={endDate || maxDate}
            isClearable={startDateClearable}
            showTimeSelect={showTimeSelect}
            timeFormat={timeFormat}
            timeIntervals={timeIntervals}
            peekNextMonth={peekNextMonth}
            showMonthDropdown={showMonthDropdown}
            showYearDropdown={showYearDropdown}
            dropdownMode={dropdownMode}
            disabled={disabled}
            className={className}
            autoComplete='off'
          />
          {separatorText &&
            <div className='slds-m-top_xx-small'>
              {separatorText}
            </div>
          }
          <DatePicker
            id='endDate'
            ref={this.endDateRef}
            selected={endDate}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            onChange={forceCloseOnSelect
              ? date => {
                  this.endDateRef.current.setOpen(false)
                  onEndDateChange(date)
                }
              : onEndDateChange}
            placeholderText={endDatePlaceholder || "End date"}
            dateFormat={dateFormat || "DD/MM/YYYY"}
            minDate={startDate || undefined}  // undefined prevents TypeError: Cannot read property 'clone' of null
            maxDate={maxDate}
            disabled={(disableEndDateInputWhenNoStartDate && !startDate) ||
              disabled
            }
            isClearable={endDateClearable}
            showTimeSelect={showTimeSelect}
            timeFormat={timeFormat}
            timeIntervals={timeIntervals}
            peekNextMonth={peekNextMonth}
            showMonthDropdown={showMonthDropdown}
            showYearDropdown={showYearDropdown}
            dropdownMode={dropdownMode}
            className={className}
            autoComplete='off'
          />
        </React.Fragment>
      )
    } else {
      return (
        <div className="slds-grid">
          <DatePicker
            ref={this.startDateRef}
            selected={startDate}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            onChange={forceCloseOnSelect
              ? date => {
                  this.startDateRef.current.setOpen(false)
                  onStartDateChange(date)
                }
              : onStartDateChange}
            placeholderText={startDatePlaceholder || "Start date"}
            dateFormat={dateFormat || "DD/MM/YYYY"}
            minDate={minDate ? moment(minDate) : undefined}
            maxDate={maxDate}
            isClearable={startDateClearable}
            showTimeSelect={showTimeSelect}
            timeFormat={timeFormat}
            timeIntervals={timeIntervals}
            peekNextMonth={peekNextMonth}
            showMonthDropdown={showMonthDropdown}
            showYearDropdown={showYearDropdown}
            dropdownMode={dropdownMode}
            disabled={disabled}
            className={className}
            autoComplete='off'
          />
          {separatorText &&
            <span className='slds-m-top_xx-small'>
              &nbsp;
              {separatorText}
              &nbsp;
            </span>
          }
          <DatePicker
            ref={this.endDateRef}
            selected={endDate}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            onChange={forceCloseOnSelect
              ? date => {
                  this.endDateRef.current.setOpen(false)
                  onEndDateChange(date)
                }
              : onEndDateChange}
            placeholderText={endDatePlaceholder || "End date"}
            dateFormat={dateFormat || "DD/MM/YYYY"}
            minDate={startDate}
            maxDate={maxDate}
            disabled={(disableEndDateInputWhenNoStartDate && !startDate) ||
              disabled
            }
            isClearable={endDateClearable}
            showTimeSelect={showTimeSelect}
            timeFormat={timeFormat}
            timeIntervals={timeIntervals}
            peekNextMonth={peekNextMonth}
            showMonthDropdown={showMonthDropdown}
            showYearDropdown={showYearDropdown}
            dropdownMode={dropdownMode}
            className={className}
            autoComplete='off'
          />
        </div>
      )
    }

  }
}



// WEBPACK FOOTER //
// ./src/components/date-range/index.js